import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight, 
  CheckCircle2, 
  Package, 
  Search, 
  FileText,
  Settings,
  Award,
  Target,
  Layers
} from 'lucide-react';

const AjioListing = () => {
  const listingServices = [
    'Product title and attribute optimization',
    'Category and sub-category mapping',
    'Product description and feature updates',
    'Image selection and catalog compliance checks',
    'Ensuring listings meet Ajio guidelines'
  ];

  const catalogFeatures = [
    'Fashion-focused product descriptions',
    'Size chart and fit guide setup',
    'Color and variant management',
    'Brand story integration',
    'SEO-optimized content'
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Ajio Product Listing & Catalog Management | Obiquiste Solutions</title>
        <meta name="description" content="Expert Ajio product listing and catalog management services. Optimized listings for fashion & lifestyle brands. Start selling on Ajio with professional help." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/ajio-product-listing-catalog" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-primary font-medium">Ajio Product Listing</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Ajio Product Listing & <span className="text-gradient">Catalog Management</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Professional product listing services for fashion and lifestyle brands on Ajio. 
                We create optimized, compliant listings that drive visibility and sales.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full text-base font-semibold">
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                  <Package className="w-7 h-7 text-purple-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-4">Product Listing Services</h2>
                <ul className="space-y-3">
                  {listingServices.map((service, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
                <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-6">
                  <Layers className="w-7 h-7 text-pink-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-4">Catalog Features</h2>
                <ul className="space-y-3">
                  {catalogFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to List Your Products on Ajio?
          </h2>
          <p className="text-purple-100 text-lg mb-8 max-w-2xl mx-auto">
            Get professional product listing services and start selling on one of India's leading fashion marketplaces.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-purple-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Contact Us Today
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AjioListing;
